import { faShip } from '@fortawesome/pro-regular-svg-icons';
import { useMutation } from '@vue/apollo-composable';
import type TripPriceLoader from '../TripPriceLoader';
import { TripSearchForm } from '@search/trip/TripSearchForm';
import {
    type Location,
    PaxType,
    type PaxCount,
    SearchMode,
    type ReserveInfo,
} from '@utils/types';
import { ReserveType, type ItineraryParams } from '@utils/itinerary';
import {
    SeaportsDocument,
    type SeaportsQuery,
    ShippingPricesDocument,
    type ShippingPricesQuery,
    ShipSearchLogDocument,
} from '@schema/directus';

export class ShipSearchForm extends TripSearchForm {
    override searchMode: SearchMode = SearchMode.Foreign;
    override domesticDestinations: Location[] = [];
    override popularDomesticOrigins: Location[] = [];
    override popularDomesticDestinations: Location[] = [];
    override popularForeignOrigins: Location[] = [];
    override popularForeignDestinations: Location[] = [];
    seaportsList: SeaportsQuery['Seaports'] = [];
    currentShippingPrices: ShippingPricesQuery['ShippingPrices'] = [];

    override initPaxCounts: PaxCount[] = [
        {
            code: PaxType.Adult,
            title: 'بزرگسال',
            subtitle: 'بزرگتر از ۱۲ سال',
            count: 1,
        },
        {
            code: PaxType.Child,
            title: 'کودک',
            subtitle: 'بین ۲ الی ۱۲ سال',
            count: 0,
        },
        {
            code: PaxType.Infant,
            title: 'نوزاد',
            subtitle: 'کوچکتر از ۲ سال',
            count: 0,
        },
        {
            code: PaxType.Seat,
            title: 'خودرو',
            subtitle: 'تعداد',
            count: 0,
        },
    ];

    get originName(): string {
        let originName = this.origin?.title;
        originName = originName?.replace(/(.+) - (?:پایانه )?(.+)/, '$1 ($2)');
        return originName || '';
    }

    get destinationName(): string {
        let destinationName = this.destination?.title;
        destinationName = destinationName?.replace(
            /(.+) - (?:پایانه )?(.+)/,
            '$1 ($2)'
        );
        return destinationName || '';
    }

    get carCount() {
        const count = this.initPaxCounts.find(
            count => count.code === PaxType.Seat
        );
        return count?.count || 0;
    }

    override get reserveInfo(): ReserveInfo {
        return {
            type: ReserveType.Ship,
            title: 'کشتی',
            nameE: 'کشتیرانی',
            icon: faShip,
        };
    }

    override get foreignOrigins(): Location[] {
        return this.seaportsList.map(sp => ({
            code: sp.Code,
            title: sp.Title || '',
            countryName: sp.CountryName || '',
        }));
    }

    override get foreignDestinations(): Location[] {
        if (!this.origin) {
            return [];
        }
        return this.seaportsList.map(sp => ({
            code: sp.Code,
            title: sp.Title || '',
            countryName: sp.CountryName || '',
        }));
    }

    override get domesticOrigins(): Location[] {
        return [
            {
                title: 'بندرخرمشهر',
                code: '',
                countryName: '',
            },
            {
                title: 'بندرلنگه',
                code: '',
                countryName: '',
            },
            {
                title: 'بندرعباس',
                code: '',
                countryName: '',
            },
        ];
    }

    override generateViewUrl(): string {
        throw new Error('The method is not implemented.');
    }

    override getPriceLoader(): TripPriceLoader {
        throw new Error('The method is not implemented.');
    }

    override get recentLocations() {
        return [];
    }

    override get viewUrl(): string {
        return '/ship-ticket';
    }

    override get serviceIndexUrl(): string {
        return '/ship-ticket';
    }

    async loadSeaports() {
        try {
            this.isLoadingLocations = true;
            const { result } = useAwaitQuery(SeaportsDocument);
            const { data } = await result();
            this.seaportsList = data.Seaports;
            this.popularForeignOrigins = this.foreignOrigins;
            this.popularForeignDestinations = this.foreignDestinations;
        } catch (e) {
            reportException(e);
        } finally {
            this.isLoadingLocations = false;
        }
    }

    override async searchLocations(
        searchKeyword: string,
        options: { target: 'origin' | 'destination'; mode: SearchMode } = {
            mode: SearchMode.Domestic,
            target: 'origin',
        }
    ) {
        if (!this.seaportsList.length) {
            await this.loadSeaports();
        }
        await super.searchLocations(searchKeyword, options);
    }

    override parsePath(): ItineraryParams {
        throw new Error('The method is not implemented.');
    }

    async loadShippingPrice({
        originCode,
        destinationCode,
    }: {
        originCode: string;
        destinationCode: string;
    }) {
        this.currentShippingPrices = [];
        const { result } = useAwaitQuery(ShippingPricesDocument, {
            FromCode: originCode,
            ToCode: destinationCode,
        });
        const { data } = await result();
        this.currentShippingPrices = data.ShippingPrices || [];
        const fromSeaport = this.seaportsList.find(s => s.Code === originCode)!;
        const toSeaport = this.seaportsList.find(
            s => s.Code === destinationCode
        )!;
        const { mutate } = useMutation(ShipSearchLogDocument, {
            variables: { To: toSeaport.Code, From: fromSeaport.Code },
        });
        if (!data.ShippingPrices?.length) {
            mutate({ IsAvailable: false });
            throw new MrError('مسیر مورد نظر موجود نمی‌باشد.', true);
        } else {
            mutate({ IsAvailable: true });
        }
    }
}
